import logger from '@eventbrite/client-logger';
import { CustomSubmissionError } from '@eventbrite/redux-form-tools';
import { asyncWait } from '@eventbrite/wait';
import { OrderDocumentStatus, TlzEnvironment } from '../../api/constants';
import { getOrderStatus } from '../../api/order';
import { getEllapsedTimeInMillisecondsFactory } from '../../utils/time';
import {
    ORDER_STATUS_POLLING_PERIOD_IN_MS,
    ORDER_STATUS_POLLING_TIMEOUT_IN_MS,
    SLOW_ORDER_STATUS_POLLING_DURATION_IN_MS,
} from './constants';

class CheckoutPollingError extends Error {
    constructor(message: string, options?: any) {
        super(message, options);
        Object.setPrototypeOf(this, CheckoutPollingError.prototype);
        this.name = 'CheckoutPollingError';
    }
}

const isOrderProcessed = (status: OrderDocumentStatus | null) =>
    status &&
    [
        OrderDocumentStatus.complete,
        OrderDocumentStatus.cancelled,
        OrderDocumentStatus.retryPayment,
        OrderDocumentStatus.expired,
    ].includes(status);

const getTimer = () => {
    const getEllapsedTimeInMilliseconds =
        getEllapsedTimeInMillisecondsFactory();

    const isTimerExpired = () =>
        getEllapsedTimeInMilliseconds() > ORDER_STATUS_POLLING_TIMEOUT_IN_MS;
    const isPollingBeingSlow = () =>
        getEllapsedTimeInMilliseconds() >
        SLOW_ORDER_STATUS_POLLING_DURATION_IN_MS;

    return {
        getEllapsedTimeInMilliseconds,
        isTimerExpired,
        isPollingBeingSlow,
    };
};

export const waitUntilOrderIsProcessed = async (
    orderId: string,
    env: TlzEnvironment,
) => {
    const {
        getEllapsedTimeInMilliseconds,
        isPollingBeingSlow,
        isTimerExpired,
    } = getTimer();

    let lastStatus: OrderDocumentStatus | null = null;

    const getErrorMetadata = () => ({
        tags: {
            order_id: orderId,
            last_order_status: lastStatus,
            total_polling_time: getEllapsedTimeInMilliseconds(),
        },
    });

    while (!isTimerExpired()) {
        const pollingPeriodPromise = asyncWait(
            ORDER_STATUS_POLLING_PERIOD_IN_MS,
        );

        try {
            lastStatus = await getOrderStatus(orderId, env);
        } catch (error) {
            lastStatus = null;

            logger.error(
                new CheckoutPollingError('Error polling CheckoutStatusAPI', {
                    cause: error.message,
                    rawError: JSON.stringify(error),
                }),
                getErrorMetadata(),
            );
        }

        if (isOrderProcessed(lastStatus)) {
            if (isPollingBeingSlow()) {
                logger.error(
                    new CheckoutPollingError(
                        `The order took ${getEllapsedTimeInMilliseconds()}ms to finish with status ${lastStatus}`,
                    ),
                    getErrorMetadata(),
                );
            }

            return lastStatus;
        } else if (isPollingBeingSlow()) {
            logger.error(
                new CheckoutPollingError(
                    `The order is taking more than ${SLOW_ORDER_STATUS_POLLING_DURATION_IN_MS}ms to finish (last status was ${lastStatus})`,
                ),
                getErrorMetadata(),
            );
        }

        await pollingPeriodPromise;
    }

    logger.error(
        new CheckoutPollingError(
            `The order did not transition to complete, cancelled or error status on time (last status was ${lastStatus})`,
        ),
        getErrorMetadata(),
    );

    throw new CustomSubmissionError(
        {
            _error: 'There was a problem submitting your order. Please try again.',
        },
        {},
    );
};
